import { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '';

export function useIntegration(token) {
  const [settings, setSettings] = useState(null);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch integration settings
  const fetchSettings = async () => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/integration/1c/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized');
        }
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setSettings(data);
      return data;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Generate new API key
  const generateApiKey = async (name = '1C Integration') => {
    if (!token) return null;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/1c/api-key/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: `name=${encodeURIComponent(name)}`,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Link API key to integration settings
  const linkApiKey = async (apiKeyId) => {
    if (!token) return false;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/integration/1c/link-api-key/${apiKeyId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }

      await fetchSettings(); // Refresh settings
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Update integration settings
  const updateSettings = async (newSettings) => {
    if (!token) return false;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/integration/1c/settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          is_enabled: newSettings.is_enabled,
          server_url: newSettings.server_url || null,
          auto_export_enabled: newSettings.auto_export_enabled || false,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }

      const data = await response.json();
      setSettings(data);
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Fetch integration logs
  const fetchLogs = async (limit = 50) => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/integration/1c/logs?limit=${limit}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setLogs(data);
      return data;
    } catch (err) {
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Fetch integration statistics
  const fetchStats = async () => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/integration/1c/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setStats(data);
      return data;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch settings on mount if token is available
  useEffect(() => {
    if (token) {
      fetchSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return {
    settings,
    logs,
    stats,
    loading,
    error,
    fetchSettings,
    generateApiKey,
    linkApiKey,
    updateSettings,
    fetchLogs,
    fetchStats,
  };
}
